import { proxy, subscribe } from "valtio";
import { type SuggestTaskResponse } from "./lib/parallel-api";

export type TaskBasis = {
  runId: string;
  basis: readonly any[];
  confidence?: "high" | "medium" | "low";
};

export type Company = {
  id: string;
  name: string;
  domain: string;
  [key: string]: string | number | boolean | string[];
};

export type Workspace = {
  id: string;
  [key: string]: string | number | boolean | string[];
};

interface AppState {
  companies: Company[];
  workspaces: Workspace[];
  workspaceColumns: string[]; // Column names for workspaces
  customColumns: string[];
  credits: number;
  suggestedTask: SuggestTaskResponse | null;
  enrichmentState: Record<string, Record<string, boolean>>;
  lastEnrichmentColumns: string[];
  taskBasisData: Record<string, Record<string, TaskBasis>>;
  columnEnrichmentConfig: Record<string, { prompt: string; provider: 'exa' | 'parallel' | 'anthropic' }>; // columnName -> enrichment config
  activeView: 'companies' | 'workspaces';
  showOnboarding: boolean;
  onboardingCompleted: boolean;
  completedVideoIds: string[];
}

const defaultState: AppState = {
  companies: [
    {
      id: "1",
      name: "Vercel",
      domain: "vercel.com",
      lastInteraction: "11 days ago",
      description: "Get ready to groove with this dance challenge!",
      categories: ["Social", "Technology"],
    },
    {
      id: "2",
      name: "Figma",
      domain: "frello.com",
      lastInteraction: "about 2 months ago",
      description: "AngelList is a platform that connects startup founders with investors.",
      categories: ["Finance", "Technology"],
    },
    {
      id: "3",
      name: "Facebook",
      domain: "facebook.com",
      lastInteraction: "6 days ago",
      description: "Connecting with friends, family, and new acquaintances.",
      categories: ["Social", "Technology"],
    },
    {
      id: "4",
      name: "Stripe",
      domain: "stripe.com",
      lastInteraction: "about 2 hours ago",
      description: "Apple is a technology giant known for its sleek and innovative products.",
      categories: ["Design", "Technology"],
    },
    {
      id: "5",
      name: "Netflix",
      domain: "netflix.com",
      lastInteraction: "22 days ago",
      description: "Netflix is a leading global provider of streaming entertainment.",
      categories: ["TV", "Technology"],
    },
    {
      id: "6",
      name: "Shopify",
      domain: "shopify.com",
      lastInteraction: "25 min ago",
      description: "Our brand is a vibrant fusion of modern trends and timeless elegance.",
      categories: ["Finance", "Technology"],
    },
    {
      id: "7",
      name: "Mailchimp",
      domain: "mailchimp.com",
      lastInteraction: "about 17 hours ago",
      description: "Mailchimp is a popular email marketing service.",
      categories: ["Email", "Technology"],
    },
    {
      id: "8",
      name: "Instagram",
      domain: "instagram.com",
      lastInteraction: "1 day ago",
      description: "Exploring hidden gems and embracing the unknown.",
      categories: ["Social", "Technology"],
    },
  ],
  workspaces: [],
  workspaceColumns: [],
  customColumns: [],
  credits: 1000,
  suggestedTask: null,
  enrichmentState: {},
  lastEnrichmentColumns: [],
  taskBasisData: {},
  columnEnrichmentConfig: {},
  activeView: 'companies',
  showOnboarding: true,
  onboardingCompleted: false,
  completedVideoIds: [],
};

const loadedState = JSON.parse(localStorage.getItem('react-vite-app-store') || 'null');
export const store = proxy<AppState>(
  loadedState
    ? {
        ...defaultState,
        ...loadedState,
        taskBasisData: loadedState.taskBasisData || {},
        columnEnrichmentConfig: loadedState.columnEnrichmentConfig || {},
        workspaces: loadedState.workspaces || [],
        workspaceColumns: loadedState.workspaceColumns || [],
        activeView: loadedState.activeView || 'companies',
        showOnboarding: loadedState.onboardingCompleted ? false : true,
        onboardingCompleted: loadedState.onboardingCompleted || false,
        completedVideoIds: loadedState.completedVideoIds || []
      }
    : defaultState
);

export const actions = {
  addCompany: (company: Omit<Company, "id">) => {
    const newCompany = {
      ...company,
      id: (store.companies.length + 1).toString(),
    } as Company;
    store.companies.push(newCompany);
  },

  updateCompany: (id: string, updates: Partial<Company>) => {
    const companyIndex = store.companies.findIndex((company) => company.id === id);
    if (companyIndex !== -1) {
      Object.assign(store.companies[companyIndex], updates);
    }
  },

  deleteCompany: (id: string) => {
    const companyIndex = store.companies.findIndex((company) => company.id === id);
    if (companyIndex !== -1) {
      store.companies.splice(companyIndex, 1);
    }
  },

  addColumn: (columnName: string, enrichmentPrompt?: string, provider?: 'exa' | 'parallel' | 'anthropic') => {
    if (store.activeView === 'workspaces') {
      // Add column to workspaces
      if (!store.workspaceColumns.includes(columnName)) {
        store.workspaceColumns.push(columnName);
        store.workspaces.forEach((workspace) => {
          workspace[columnName] = "";
        });

        // Store enrichment config if provided
        if (enrichmentPrompt && provider) {
          store.columnEnrichmentConfig[columnName] = { prompt: enrichmentPrompt, provider };
        }
      }
    } else {
      // Add column to companies
      if (!store.customColumns.includes(columnName)) {
        store.customColumns.push(columnName);
        store.companies.forEach((company) => {
          company[columnName] = "";
        });

        // Store enrichment config if provided
        if (enrichmentPrompt && provider) {
          store.columnEnrichmentConfig[columnName] = { prompt: enrichmentPrompt, provider };
        }
      }
    }
  },

  removeColumn: (columnName: string) => {
    if (store.activeView === 'workspaces') {
      // Remove column from workspaces
      const columnIndex = store.workspaceColumns.indexOf(columnName);
      if (columnIndex !== -1) {
        store.workspaceColumns.splice(columnIndex, 1);
        store.workspaces.forEach((workspace) => {
          delete workspace[columnName];
        });
        // Remove enrichment config if exists
        delete store.columnEnrichmentConfig[columnName];
      }
    } else {
      // Remove column from companies
      const columnIndex = store.customColumns.indexOf(columnName);
      if (columnIndex !== -1) {
        store.customColumns.splice(columnIndex, 1);
        store.companies.forEach((company) => {
          delete company[columnName];
        });
        // Remove enrichment config if exists
        delete store.columnEnrichmentConfig[columnName];
      }
    }
  },

  renameColumn: (oldName: string, newName: string) => {
    if (store.activeView === 'workspaces') {
      // Rename column in workspaces
      const columnIndex = store.workspaceColumns.indexOf(oldName);
      if (columnIndex !== -1 && !store.workspaceColumns.includes(newName)) {
        // Update column name in workspaceColumns array
        store.workspaceColumns[columnIndex] = newName;

        // Update all workspaces to use the new column name
        store.workspaces.forEach((workspace) => {
          if (oldName in workspace) {
            workspace[newName] = workspace[oldName];
            delete workspace[oldName];
          }
        });

        // Update enrichment config if exists
        if (store.columnEnrichmentConfig[oldName]) {
          store.columnEnrichmentConfig[newName] = store.columnEnrichmentConfig[oldName];
          delete store.columnEnrichmentConfig[oldName];
        }

        // Update task basis data if exists
        Object.keys(store.taskBasisData).forEach((id) => {
          if (store.taskBasisData[id][oldName]) {
            store.taskBasisData[id][newName] = store.taskBasisData[id][oldName];
            delete store.taskBasisData[id][oldName];
          }
        });

        // Update enrichment state if exists
        Object.keys(store.enrichmentState).forEach((id) => {
          if (store.enrichmentState[id][oldName]) {
            store.enrichmentState[id][newName] = store.enrichmentState[id][oldName];
            delete store.enrichmentState[id][oldName];
          }
        });
      }
    } else {
      // Rename column in companies
      const columnIndex = store.customColumns.indexOf(oldName);
      if (columnIndex !== -1 && !store.customColumns.includes(newName)) {
        // Update column name in customColumns array
        store.customColumns[columnIndex] = newName;

        // Update all companies to use the new column name
        store.companies.forEach((company) => {
          if (oldName in company) {
            company[newName] = company[oldName];
            delete company[oldName];
          }
        });

        // Update enrichment config if exists
        if (store.columnEnrichmentConfig[oldName]) {
          store.columnEnrichmentConfig[newName] = store.columnEnrichmentConfig[oldName];
          delete store.columnEnrichmentConfig[oldName];
        }

        // Update task basis data if exists
        Object.keys(store.taskBasisData).forEach((companyId) => {
          if (store.taskBasisData[companyId][oldName]) {
            store.taskBasisData[companyId][newName] = store.taskBasisData[companyId][oldName];
            delete store.taskBasisData[companyId][oldName];
          }
        });

        // Update enrichment state if exists
        Object.keys(store.enrichmentState).forEach((companyId) => {
          if (store.enrichmentState[companyId][oldName]) {
            store.enrichmentState[companyId][newName] = store.enrichmentState[companyId][oldName];
            delete store.enrichmentState[companyId][oldName];
          }
        });
      }
    }
  },

  updateCompanyField: (
    id: string,
    fieldName: string,
    value: string | number | boolean | string[]
  ) => {
    const companyIndex = store.companies.findIndex((company) => company.id === id);
    if (companyIndex !== -1) {
      store.companies[companyIndex][fieldName] = value;
    }
  },

  decrementCredits: (amount: number = 1) => {
    store.credits = Math.max(0, store.credits - amount);
  },

  setSuggestedTask: (task: SuggestTaskResponse | null) => {
    store.suggestedTask = task;
  },

  setCompanyColumnEnriching: (companyId: string, columnName: string, isEnriching: boolean) => {
    if (!store.enrichmentState[companyId]) {
      store.enrichmentState[companyId] = {};
    }
    if (isEnriching) {
      store.enrichmentState[companyId][columnName] = true;
    } else {
      delete store.enrichmentState[companyId][columnName];
      if (Object.keys(store.enrichmentState[companyId]).length === 0) {
        delete store.enrichmentState[companyId];
      }
    }
  },

  clearEnrichmentState: () => {
    store.enrichmentState = {};
  },

  setLastEnrichmentColumns: (columns: string[]) => {
    store.lastEnrichmentColumns = columns;
  },

  undoLastEnrichment: () => {
    store.lastEnrichmentColumns.forEach(columnName => {
      actions.removeColumn(columnName);
    });
    store.lastEnrichmentColumns = [];
  },

  setTaskBasis: (companyId: string, columnName: string, runId: string, basis: any[], confidence?: "high" | "medium" | "low") => {
    if (!store.taskBasisData[companyId]) {
      store.taskBasisData[companyId] = {};
    }
    store.taskBasisData[companyId][columnName] = { runId, basis, confidence };
  },

  getTaskBasis: (companyId: string, columnName: string): TaskBasis | null => {
    return store.taskBasisData[companyId]?.[columnName] || null;
  },

  // Workspace actions
  setActiveView: (view: 'companies' | 'workspaces') => {
    store.activeView = view;
  },

  importWorkspacesFromCSV: (data: { columns: string[]; rows: Workspace[] }) => {
    store.workspaces = data.rows;
    store.workspaceColumns = data.columns;
  },

  addWorkspace: (workspace: Omit<Workspace, "id">) => {
    const newWorkspace = {
      ...workspace,
      id: (store.workspaces.length + 1).toString(),
    } as Workspace;
    store.workspaces.push(newWorkspace);
  },

  updateWorkspaceField: (
    id: string,
    fieldName: string,
    value: string | number | boolean | string[]
  ) => {
    const workspaceIndex = store.workspaces.findIndex((workspace) => workspace.id === id);
    if (workspaceIndex !== -1) {
      store.workspaces[workspaceIndex][fieldName] = value;
    }
  },

  deleteWorkspace: (id: string) => {
    const workspaceIndex = store.workspaces.findIndex((workspace) => workspace.id === id);
    if (workspaceIndex !== -1) {
      store.workspaces.splice(workspaceIndex, 1);
    }
  },

  clearWorkspaces: () => {
    store.workspaces = [];
    store.workspaceColumns = [];
    // Clear workspace-related enrichment data
    Object.keys(store.taskBasisData).forEach((id) => {
      if (!store.companies.find(c => c.id === id)) {
        delete store.taskBasisData[id];
      }
    });
    Object.keys(store.enrichmentState).forEach((id) => {
      if (!store.companies.find(c => c.id === id)) {
        delete store.enrichmentState[id];
      }
    });
  },

  // Onboarding actions
  setShowOnboarding: (show: boolean) => {
    store.showOnboarding = show;
  },

  completeOnboarding: () => {
    store.showOnboarding = false;
    store.onboardingCompleted = true;
  },

  markVideoCompleted: (videoId: string) => {
    if (!store.completedVideoIds.includes(videoId)) {
      store.completedVideoIds.push(videoId);
    }
  },
};

// Subscribe to store changes and save to localStorage
subscribe(store, () => {
  localStorage.setItem('react-vite-app-store', JSON.stringify(store));
});
